import { useState } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import {
  AccountsListPageDocument,
  BankingOnboardingFragment,
  ConnectBankDocument,
  CreateBankingManualAccountDocument,
  CreateBankingOnboardingDocument,
  CreateForCardsBankingOnboardingDocument,
  CurrencySelectorCurrencyFragment,
  InstitutionFragment,
  LegalEntityForSelectFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { defaultBankNumberFormat } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { getDefaultAlias } from 'modules/banking/utils'

import { WizardData } from './types'
import { BankAccountConnectionWizardStepF0 } from './wizardSteps/BankAccountConnectionWizardStepF0'
import { BankAccountManualInformationWizardStepF0 } from './wizardSteps/BankAccountManualInformationWizardStepF0'
import { BankAccountSelectorWizardStepF0 } from './wizardSteps/BankAccountSelectorWizardStepF0'
import { BankAccountTypeSelectorWizardStepF0 } from './wizardSteps/BankAccountTypeSelectorWizardStepF0'
import { ExternalOnboardingExplanationStepF0 } from './wizardSteps/ExternalOnboardingExplanationStepF0'
import { LedgerAccountLinkStepF0 } from './wizardSteps/LedgerAccountLinkStepF0'
import { MembershipsStep } from './wizardSteps/MembershipsStep'

interface AddAccountModalF0Props {
  onClose: () => void
  legalEntities: LegalEntityForSelectFragment[]
  currencies: CurrencySelectorCurrencyFragment[]
  accountingEnabled: boolean
  existingOnboardings: BankingOnboardingFragment[]
  showAccountPurposeSelector: boolean
}

export const AddAccountModalF0 = ({
  onClose,
  legalEntities,
  currencies,
  accountingEnabled,
  existingOnboardings,
}: AddAccountModalF0Props) => {
  const defaultLegalEntity = legalEntities.length === 1 ? legalEntities[0] ?? null : null

  // Track selected creation source to determine which steps to show
  const [selectedCreationSource, setSelectedCreationSource] = useState<string | null>(null)
  // Track if user selected a bank or "Add manually"
  const [showConnectStep, setShowConnectStep] = useState<boolean>(false)
  // Track validation errors for manual account step
  const [accountNumberErrors, setAccountNumberErrors] = useState<{
    accountNumber?: string
    complementaryData?: string
  }>({})

  const trackAddAccount = (account_type: 'manual' | 'automatic') => {
    track(trackingEvents.accounting.banking.addBankAccount, { account_type })
  }

  // Mutations for Stripe/Swan
  const [createForCards] = useMutation(CreateForCardsBankingOnboardingDocument, {
    extractorFn: (data) => data.bankingMutations.createForCardsBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  const [createForPayments] = useMutation(CreateBankingOnboardingDocument, {
    extractorFn: (data) => data.bankingMutations.createForPaymentsBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  // Mutation for GoCardless
  const [connectBank] = useMutation(ConnectBankDocument, {
    extractorFn: (data) => data.bankingMutations.createForExternalBankBankingOnboarding,
    refetchQueries: [AccountsListPageDocument],
  })

  // Mutation for manual account creation
  const [createManualAccount] = useMutation(CreateBankingManualAccountDocument, {
    extractorFn: (data) => data.bankingMutations.createManualBankingBankAccount,
    refetchQueries: [AccountsListPageDocument],
  })

  const isUnhandledResult = (value: unknown): value is UnhandledResult<Result<unknown>> => {
    return value !== null && typeof value === 'object'
  }

  const handleOnboardingResult = useUnsafeCallback((result: unknown) => {
    if (!isUnhandledResult(result)) {
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.accounts.new_wizard.notifications.error'),
      })
      return
    }

    // handleResult shows flash for SimpleErrors automatically
    const handled = handleResult(result, () => {
      // This callback only runs for StructuredErrors (form errors)
      app.setProgress(null)
    })

    if (!handled.success) {
      // Clear progress for any error (SimpleError or StructuredError)
      app.setProgress(null)
      return
    }

    if (handled.data && typeof handled.data === 'object' && 'onboarding' in handled.data) {
      const dataObj = handled.data
      const onboarding = 'onboarding' in dataObj ? dataObj.onboarding : null
      const newOnboardingUrl =
        onboarding &&
        typeof onboarding === 'object' &&
        'onboardingUrl' in onboarding &&
        typeof onboarding.onboardingUrl === 'string'
          ? onboarding.onboardingUrl
          : null
      if (newOnboardingUrl) {
        app.setProgress(null)
        window.open(newOnboardingUrl, '_self')
      } else {
        // Success but no onboarding URL - clear progress and show error
        app.setProgress(null)
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.accounts.new_wizard.notifications.error'),
        })
      }
    }
  }, [])

  const handleSubmit = useUnsafeCallback(
    async (data: WizardData) => {
      if (!data.legalEntity || !data.creationSource) return

      // Stripe (expenses, software, benefits)
      if (
        data.creationSource === 'expenses' ||
        data.creationSource === 'software' ||
        data.creationSource === 'benefits'
      ) {
        const result = await createForCards({
          variables: {
            legalEntityId: data.legalEntity.id,
            source: data.creationSource,
          },
        })
          .then((res) => {
            trackAddAccount('automatic')
            return res
          })
          .catch(() => {
            app.setProgress(null)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.new_wizard.notifications.error'),
            })
            return null
          })

        if (result) {
          handleOnboardingResult(result)
        }
      }
      // Swan (payments)
      else if (data.creationSource === 'payments') {
        const result = await createForPayments({
          variables: {
            legalEntityId: data.legalEntity.id,
          },
        })
          .then((res) => {
            trackAddAccount('automatic')
            return res
          })
          .catch(() => {
            app.setProgress(null)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.new_wizard.notifications.error'),
            })
            return null
          })

        if (result) {
          handleOnboardingResult(result)
        }
      }
      // GoCardless (finance) - connect to bank
      else if (
        (data.creationSource === 'finance' || data.creationSource === 'accounts_payable') &&
        data.institution
      ) {
        app.setProgress({
          message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
        })
        const result = await connectBank({
          variables: {
            institutionId: data.institution.id,
            legalEntityId: data.legalEntity.id,
          },
        })
          .then((res) => {
            trackAddAccount('automatic')
            return res
          })
          .catch(() => {
            app.setProgress(null)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.new_wizard.notifications.error'),
            })
            return null
          })

        if (result) {
          handleOnboardingResult(result)
        }
      }
      // GoCardless (finance) - manual account creation
      else if (
        (data.creationSource === 'finance' || data.creationSource === 'accounts_payable') &&
        !data.institution
      ) {
        const result = await createManualAccount({
          variables: {
            legalEntityId: String(data.legalEntity.id),
            accountAlias: data.accountAlias,
            accountNumber: data.accountNumber,
            complementaryData: data.complementaryData || undefined,
            currency: data.currency,
            accountNumberType: data.accountNumberType,
            ledgerAccountId: data.ledgerAccountId || undefined,
            employeeSelection: data.employeeSelection,
          },
        })
          .then((res) => {
            trackAddAccount('manual')
            return res
          })
          .catch(() => {
            app.setProgress(null)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.new_wizard.notifications.error'),
            })
            return null
          })

        if (!result) {
          return
        }

        if (!isUnhandledResult(result)) {
          app.setProgress(null)
          return
        }

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('banking.accounts.new_wizard.notifications.error'),
          })
          app.setProgress(null)
        })

        if (handled.success) {
          app.setProgress(null)
          app.setFlash({
            type: 'correct',
            message: i18n.t('banking.accounts.new_wizard.notifications.success'),
          })
          onClose()
        }
      }
    },
    [
      createForCards,
      createForPayments,
      connectBank,
      createManualAccount,
      handleOnboardingResult,
      onClose,
    ]
  )

  const handleInstitutionSelect = useUnsafeCallback((institution: InstitutionFragment | null) => {
    setShowConnectStep(!!institution)
  }, [])

  // Wrapper for onDataChanged that also syncs our local state
  const createWrappedOnDataChanged = useUnsafeCallback(
    (originalOnDataChanged: (data: WizardData) => void) => {
      return (newData: WizardData) => {
        // Sync creation source state for dynamic steps
        if (newData.creationSource !== selectedCreationSource) {
          setSelectedCreationSource(newData.creationSource)
        }
        originalOnDataChanged(newData)
      }
    },
    [selectedCreationSource]
  )

  // Wizard steps
  const accountSetupStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_1.side_title'),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      isCompleted: (data) => !!data.legalEntity && !!data.creationSource,
      render: (props) => (
        <BankAccountTypeSelectorWizardStepF0
          data={props.data}
          onDataChanged={createWrappedOnDataChanged(props.onDataChanged)}
          completed={props.completed}
          loading={props.loading}
          existingOnboardings={existingOnboardings}
        />
      ),
    }),
    [existingOnboardings, createWrappedOnDataChanged]
  )

  // For GoCardless: Bank selector step
  const bankSelectorStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_2.title'),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      isCompleted: () => true,
      render: (props) => (
        <BankAccountSelectorWizardStepF0
          data={props.data}
          onDataChanged={props.onDataChanged}
          completed={props.completed}
          loading={props.loading}
          onSelect={handleInstitutionSelect}
        />
      ),
    }),
    [handleInstitutionSelect]
  )

  // For GoCardless with bank selected: Bank connection step
  const bankConnectionStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_1.title'),
      nextText: i18n.t('banking.accounts.new_wizard.finish_and_synchronize'),
      isCompleted: () => true,
      render: (props) => (
        <BankAccountConnectionWizardStepF0
          data={props.data}
          onDataChanged={props.onDataChanged}
          completed={props.completed}
          loading={props.loading}
        />
      ),
    }),
    []
  )

  // For GoCardless with "Add manually": Manual account information step
  const manualAccountStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_0.title'),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      isCompleted: (data) =>
        !!data.accountNumber &&
        !!data.currency &&
        !accountNumberErrors.accountNumber &&
        !accountNumberErrors.complementaryData,
      render: (props) => (
        <BankAccountManualInformationWizardStepF0
          data={props.data}
          onDataChanged={props.onDataChanged}
          completed={props.completed}
          loading={props.loading}
          currencies={currencies}
          errors={accountNumberErrors}
          onErrorsChange={setAccountNumberErrors}
        />
      ),
    }),
    [currencies, accountNumberErrors]
  )

  // For manual account: Ledger account link step (only if accounting is enabled)
  const ledgerAccountStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_2.title'),
      nextText: i18n.t('banking.accounts.new_wizard.next'),
      isCompleted: () => true,
      render: (props) => (
        <LedgerAccountLinkStepF0
          data={props.data}
          onDataChanged={props.onDataChanged}
          completed={props.completed}
          loading={props.loading}
        />
      ),
    }),
    []
  )

  // For manual account: Memberships step
  const membershipsStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.finish_wizard.step_3.title'),
      nextText: i18n.t('banking.accounts.new_wizard.create_account'),
      isCompleted: () => true,
      render: (props) => (
        <MembershipsStep
          data={props.data}
          onDataChanged={props.onDataChanged}
          completed={props.completed}
          loading={props.loading}
        />
      ),
    }),
    []
  )

  // For Stripe/Swan: External onboarding explanation step
  const externalOnboardingStep: WizardStep<WizardData> = useUnsafeMemo(
    () => ({
      title: i18n.t('banking.accounts.new_wizard.step_3_1.side_title'),
      isCompleted: () => true,
      nextText: i18n.t('banking.accounts.new_wizard.finish_and_synchronize'),
      render: (props) =>
        props.data.creationSource ? (
          <ExternalOnboardingExplanationStepF0
            creationSource={props.data.creationSource}
            legalEntity={props.data.legalEntity}
          />
        ) : null,
    }),
    []
  )

  // Determine steps based on selected creation source and whether a bank was selected
  const steps = useUnsafeMemo(() => {
    // GoCardless (finance) with bank selected: accountSetup → bankSelector → bankConnection
    if (
      (selectedCreationSource === 'finance' || selectedCreationSource === 'accounts_payable') &&
      showConnectStep
    ) {
      return [accountSetupStep, bankSelectorStep, bankConnectionStep]
    }

    // GoCardless (finance) with "Add manually": accountSetup → bankSelector → manualAccount → ledgerAccount (if enabled) → memberships
    if (
      (selectedCreationSource === 'finance' || selectedCreationSource === 'accounts_payable') &&
      !showConnectStep
    ) {
      const manualSteps = [accountSetupStep, bankSelectorStep, manualAccountStep]
      if (accountingEnabled) {
        manualSteps.push(ledgerAccountStep)
      }
      manualSteps.push(membershipsStep)
      return manualSteps
    }

    // Stripe/Swan (expenses, software, benefits, payments): accountSetup → externalOnboarding
    if (
      selectedCreationSource === 'expenses' ||
      selectedCreationSource === 'software' ||
      selectedCreationSource === 'benefits' ||
      selectedCreationSource === 'payments'
    ) {
      return [accountSetupStep, externalOnboardingStep]
    }

    // Default: just show account setup step until a source is selected
    return [accountSetupStep, externalOnboardingStep]
  }, [
    selectedCreationSource,
    showConnectStep,
    accountingEnabled,
    accountSetupStep,
    bankSelectorStep,
    bankConnectionStep,
    manualAccountStep,
    ledgerAccountStep,
    membershipsStep,
    externalOnboardingStep,
  ])

  const initialData: WizardData = useUnsafeMemo(
    () => ({
      legalEntity: defaultLegalEntity,
      currency: defaultLegalEntity?.currency || 'EUR',
      accountNumberType: defaultLegalEntity?.country
        ? defaultBankNumberFormat(defaultLegalEntity.country)
        : defaultBankNumberFormat('es'),
      accountNumber: '',
      accountAlias: getDefaultAlias('finance'),
      complementaryData: '',
      institution: null,
      search: '',
      country: defaultLegalEntity?.country.toLocaleUpperCase() || null,
      ledgerAccountId: null,
      ledgerAccountName: null,
      creationSource: null,
      employeeSelection: { type: 'manual', ids: [] },
    }),
    [defaultLegalEntity]
  )

  return (
    <Wizard.Modal<WizardData>
      title={i18n.t('banking.accounts.actions.add_account')}
      steps={steps}
      initialData={initialData}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  )
}
